import { queryOptions } from '@tanstack/react-query'
import { apiClient } from '../client'
import type {
  Connector,
  DriveItem,
  ClickUpNode,
  ConnectorProvider,
  DisconnectPreview,
} from '../types'
import { providerRouteSegment } from '../types'

/** Extract array from API response — handles raw arrays, { data: [] }, { files: [] }, { items: [] } */
function unwrapArray<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res
  if (res && typeof res === 'object') {
    const obj = res as Record<string, unknown>
    for (const key of ['data', 'files', 'items']) {
      if (Array.isArray(obj[key])) return obj[key] as T[]
    }
  }
  return []
}

export const connectorQueries = {
  list: () =>
    queryOptions({
      queryKey: ['connectors', 'list'],
      queryFn: async () => {
        const res = await apiClient<unknown>('/api/v1/connectors')
        return unwrapArray<Connector>(res)
      },
      staleTime: 1000 * 30,
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: ['connectors', 'detail', id],
      queryFn: () => apiClient<Connector>(`/api/v1/connectors/${id}`),
      staleTime: 1000 * 60,
    }),

  browseGoogle: (connectorId: string, folderId?: string) =>
    queryOptions({
      queryKey: ['connectors', connectorId, 'browse', 'google', folderId ?? 'root'],
      queryFn: async () => {
        const params = new URLSearchParams({ connectorId })
        if (folderId) params.set('folderId', folderId)
        // API returns { items, sharedDrives? } — preserve both. sharedDrives
        // is only populated when browsing at root (folderId omitted) and lets
        // the export-side picker render Shared Drives as additional roots.
        const res = await apiClient<{
          items?: DriveItem[]
          files?: DriveItem[]
          data?: DriveItem[]
          sharedDrives?: Array<{ id: string; name: string }>
        }>(`/api/v1/connectors/google/browse?${params.toString()}`, {
          // A 401 here means Drive denied access to this folder (e.g. an
          // unshared "My Drive"), not Auth0 session expiry. Don't log the
          // user out — surface the error inline.
          skipAuthRedirectOn401: true,
        })
        const items = unwrapArray<DriveItem>(res)
        return {
          items,
          sharedDrives: Array.isArray(res?.sharedDrives) ? res.sharedDrives : [],
        }
      },
      staleTime: 30_000,
    }),

  browseClickUp: (connectorId: string, parentId?: string, parentType?: string) =>
    queryOptions({
      queryKey: ['connectors', connectorId, 'browse', 'clickup', parentId ?? 'root'],
      queryFn: async () => {
        const params = new URLSearchParams({ connectorId })
        if (parentId) params.set('parentId', parentId)
        if (parentType) params.set('parentType', parentType)
        const res = await apiClient<unknown>(
          `/api/v1/connectors/clickup/browse?${params.toString()}`,
          // 401 here = ClickUp OAuth/permission failure, not Auth0 expiry.
          { skipAuthRedirectOn401: true },
        )
        return unwrapArray<ClickUpNode>(res)
      },
      staleTime: 30_000,
    }),

  /**
   * Phase 28 (KSY-05): live impact preview for the DisconnectDialog.
   * API Phase 25 §2 — pure DB COUNT (cheap; safe to refetch every dialog open).
   *
   * `staleTime: 0` forces refetch on every mount when `enabled` flips true,
   * so reopening the dialog after another KP modification gets fresh counts.
   * Cache key entry is removed by useDisconnectConnector.onSuccess so a
   * re-disconnect attempt would re-fetch fresh.
   */
  disconnectPreview: (provider: ConnectorProvider, connectorId: string) =>
    queryOptions({
      queryKey: ['connectors', 'disconnect-preview', connectorId],
      queryFn: () =>
        apiClient<DisconnectPreview>(
          `/api/v1/connectors/${providerRouteSegment[provider]}/disconnect-preview?connectorId=${connectorId}`,
        ),
      staleTime: 0,
    }),
}
